import { useMemo, useState } from "react";
import { productDomain } from "@reina/shared";
import type { ComparableGroup } from "@reina/shared";
import {
  CodeChip,
  Button,
  EmptyState,
  Field,
  FieldGrid,
  Panel,
  PanelBody,
  PanelHead,
  Pill,
  SupplierAvatar,
  TableWrap,
  useToast,
} from "../../components";
import { useSuppliers } from "../../api/catalogue";
import { useComparableGroups, useRebuildComparables, useReviewMember } from "../../api/comparables";
import { errorMessage, formatInt, formatMoney } from "../../lib/format";
import { Pager } from "../../lib/Pager";
import {
  confidenceLabel,
  confidenceTone,
  reviewStateLabel,
  reviewStateTone,
} from "../../lib/tones";
import { useDebounced } from "../../lib/useDebounced";

const PAGE_SIZE = 25;

export function ComparablesPage() {
  const toast = useToast();
  const [domain, setDomain] = useState("");
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const debouncedSearch = useDebounced(search);

  const groups = useComparableGroups({
    ...(domain ? { domain } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(needsReviewOnly ? { needsReview: true } : {}),
    limit: PAGE_SIZE,
    offset,
  });
  const suppliers = useSuppliers();
  const rebuild = useRebuildComparables();
  const items = groups.data?.items ?? [];

  const supplierById = useMemo(
    () => new Map((suppliers.data?.items ?? []).map((s) => [s.id, s])),
    [suppliers.data]
  );

  const onRebuild = async () => {
    try {
      const result = await rebuild.mutateAsync();
      toast.push({
        message: `Rebuilt comparables: ${result.groups} groups, ${result.members} members`,
        tone: "ok",
      });
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  };

  return (
    <div className="stack">
      <Panel>
        <PanelHead
          title="Comparables"
          sub={
            groups.data
              ? `${formatInt(groups.data.total)} cross-brand group${groups.data.total === 1 ? "" : "s"}`
              : "cross-brand groups + pre-computed supplier lows"
          }
          actions={
            <Button variant="orange" onClick={() => void onRebuild()} disabled={rebuild.isPending}>
              {rebuild.isPending ? "Rebuilding…" : "Rebuild"}
            </Button>
          }
        />
        <PanelBody>
          <FieldGrid columns={3}>
            <Field label="Search">
              <input
                className="field"
                placeholder="label or type, e.g. elbow…"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setOffset(0);
                }}
              />
            </Field>
            <Field label="Domain">
              <select
                className="field"
                value={domain}
                onChange={(event) => {
                  setDomain(event.target.value);
                  setOffset(0);
                }}
              >
                <option value="">All domains</option>
                {productDomain.options.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Review">
              <span className="result-cell" style={{ minHeight: 42 }}>
                <input
                  type="checkbox"
                  checked={needsReviewOnly}
                  onChange={(event) => {
                    setNeedsReviewOnly(event.target.checked);
                    setOffset(0);
                  }}
                />
                Needs review only
              </span>
            </Field>
          </FieldGrid>
        </PanelBody>
        {groups.data && (
          <Pager total={groups.data.total} limit={PAGE_SIZE} offset={offset} onOffset={setOffset} />
        )}
      </Panel>

      {groups.isPending ? (
        <Panel>
          <PanelBody>
            <EmptyState title="Loading comparable groups…" />
          </PanelBody>
        </Panel>
      ) : groups.isError ? (
        <Panel>
          <PanelBody>
            <EmptyState title="Could not load groups" hint={errorMessage(groups.error)} />
          </PanelBody>
        </Panel>
      ) : items.length === 0 ? (
        <Panel>
          <PanelBody>
            <EmptyState
              title="No comparable groups"
              hint="Publish a price list, then hit Rebuild to bucket products across brands."
            />
          </PanelBody>
        </Panel>
      ) : (
        items.map((group) => (
          <GroupPanel
            key={group.id}
            group={group}
            supplierNameOf={(id) => supplierById.get(id)?.name ?? id.slice(0, 8)}
            supplierLogoOf={(id) => supplierById.get(id)?.logo ?? null}
          />
        ))
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One comparable group: members table + supplier-lows strip
// ---------------------------------------------------------------------------

function GroupPanel({
  group,
  supplierNameOf,
  supplierLogoOf,
}: {
  group: ComparableGroup;
  supplierNameOf: (id: string) => string;
  supplierLogoOf: (id: string) => string | null;
}) {
  const toast = useToast();
  const review = useReviewMember();
  const pendingReview = group.members.filter(
    (member) => member.needsReview && member.reviewState === "pending"
  ).length;

  const onReview = async (memberId: string, reviewState: "approved" | "rejected") => {
    try {
      await review.mutateAsync({ memberId, input: { reviewState } });
      toast.push({ message: `Membership ${reviewState}`, tone: "ok" });
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  };

  return (
    <Panel>
      <PanelHead
        title={group.label}
        sub={`${group.domain} · ${group.canonicalType}${group.sizeSignature ? ` · ${group.sizeSignature}` : ""} · ${group.members.length} member${group.members.length === 1 ? "" : "s"}`}
        actions={pendingReview > 0 ? <Pill tone="warn">{pendingReview} to review</Pill> : undefined}
      />
      <TableWrap>
        <thead>
          <tr>
            <th>Brand</th>
            <th>Description</th>
            <th>Supplier code</th>
            <th>Pack</th>
            <th>Score</th>
            <th>Confidence</th>
            <th>Review</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {group.members.map((member) => (
            <tr key={member.id}>
              <td>{member.brandName ?? "—"}</td>
              <td>
                <strong>{member.description}</strong>
                {member.rationale && <span className="sub">{member.rationale}</span>}
              </td>
              <td>
                <span className="code-stack">
                  <CodeChip code={member.iqCode} title="Supplier code" />
                  {member.manPartNumber && (
                    <CodeChip code={member.manPartNumber} dim title="Manufacturer part no." />
                  )}
                </span>
              </td>
              <td className="mono">
                {[member.uom, member.packQty != null ? `×${member.packQty}` : null]
                  .filter(Boolean)
                  .join(" ") || "—"}
              </td>
              <td className="mono">{member.score.toFixed(1)}</td>
              <td>
                <Pill tone={confidenceTone[member.confidence]}>
                  {confidenceLabel[member.confidence]}
                </Pill>
              </td>
              <td>
                <Pill tone={reviewStateTone[member.reviewState]}>
                  {reviewStateLabel[member.reviewState]}
                </Pill>
              </td>
              <td>
                <span className="actions">
                  <Button
                    mini
                    disabled={review.isPending || member.reviewState === "approved"}
                    onClick={() => void onReview(member.id, "approved")}
                  >
                    Approve
                  </Button>
                  <Button
                    mini
                    variant="danger"
                    disabled={review.isPending || member.reviewState === "rejected"}
                    onClick={() => void onReview(member.id, "rejected")}
                  >
                    Reject
                  </Button>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      <PanelBody>
        {group.supplierLows.length === 0 ? (
          <span className="sub" style={{ marginTop: 0 }}>
            No current prices in this group yet — publish a price list to populate supplier lows.
          </span>
        ) : (
          <div className="actions" style={{ justifyContent: "flex-start" }}>
            <span className="label">Supplier lows</span>
            {[...group.supplierLows]
              .sort((a, b) => a.nettPrice - b.nettPrice)
              .map((low, index) => (
                <span
                  key={low.id}
                  className="result-cell"
                  title={supplierNameOf(low.supplierId)}
                >
                  <SupplierAvatar
                    name={supplierNameOf(low.supplierId)}
                    logo={supplierLogoOf(low.supplierId)}
                    size={28}
                  />
                  <span className={index === 0 ? "money" : "mono"}>
                    {formatMoney(low.nettPrice)}
                  </span>
                  {index === 0 && <Pill tone="ok">Low</Pill>}
                </span>
              ))}
          </div>
        )}
      </PanelBody>
    </Panel>
  );
}
